import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryKeys } from '~/api/queryKeys';

import {
  fetchExperiment,
  fetchExperimentAdjustedMetrics,
  fetchExperimentArmComparison,
  fetchExperimentCategoryExclusions,
  fetchExperimentDailyLift,
  fetchExperimentInStockTrends,
  fetchExperimentKpis,
  fetchExperimentRoasComparison,
  fetchExperiments,
  fetchExperimentTimeline,
} from '../services/experiment.service';
import type { ExperimentListParams, ExperimentStats } from '../types/experiment.types';
import { EXPERIMENT_ANALYTICS_READ_OPTIONS } from '../utils/snapshot-read-retry';

import { useAnalyticsExperimentListAll } from './use-experiment-analytics';

/**
 * Paginated, filterable experiment list — drives the experiments list page.
 * `refetchOnMount: 'always'` overrides the global 5-minute staleTime so
 * navigating back to the listing (e.g. from mid-experiment via the sidebar)
 * always refetches and surfaces newly-created drafts. Within-page renders
 * (pagination, filter changes) still hit the cache because they don't remount.
 */
export function useExperiments(params: ExperimentListParams) {
  return useQuery({
    queryKey: queryKeys.experiments.list(params as unknown as Record<string, unknown>),
    queryFn: () => fetchExperiments(params),
    refetchOnMount: 'always',
  });
}

/**
 * Lightweight existence check — does the current subsidiary have at least one
 * experiment? Fetches a single-row, unfiltered page so the answer is
 * independent of any list search/filter state. `archived: 'ALL'` on purpose:
 * the default list hides archived rows, and a tenant whose every experiment is
 * archived still "has experiments" — otherwise the access gate would bounce
 * admins off `/experiments` the moment they archived the last one. Used by the
 * experiments access gate to show the list (instead of an onboarding
 * placeholder) when experiments already exist. `enabled` defers the request
 * when the caller doesn't need it (e.g. catalog import already complete).
 */
export function useHasAnyExperiments(enabled = true): {
  hasExperiments: boolean;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: queryKeys.experiments.list({
      page: 1,
      pageSize: 1,
      archived: 'ALL',
      existence: true,
    }),
    queryFn: () => fetchExperiments({ page: 1, pageSize: 1, archived: 'ALL' }),
    enabled,
  });
  return { hasExperiments: (query.data?.total ?? 0) > 0, isLoading: query.isLoading };
}

/**
 * Archived-experiment count for the Archived view's badge. There is no
 * status-counts endpoint for experiments, so a one-row `archived=ARCHIVED` page
 * supplies `totalElements`. Admin-only callers pass `enabled` so members never
 * fire it. Lives under `experiments.all`, so every archive toggle refreshes it.
 */
export function useArchivedExperimentsCount(enabled: boolean): number | undefined {
  const query = useQuery({
    queryKey: queryKeys.experiments.list({
      page: 1,
      pageSize: 1,
      archived: 'ARCHIVED',
      count: true,
    }),
    queryFn: () => fetchExperiments({ page: 1, pageSize: 1, archived: 'ARCHIVED' }),
    enabled,
  });
  return query.data?.total;
}

/**
 * Portfolio-level summary cards derived client-side from the analytics
 * portfolio list. No `/experiments/stats` endpoint exists upstream, so the
 * aggregation runs here instead of fabricating numbers in the service layer.
 *
 * Fields the analytics portfolio item does not expose (platform breakdown,
 * absolute revenue impact, critical-vs-warning split) intentionally stay
 * empty / zero — fake breakdowns hid here for months and we are not
 * reintroducing them. Once analytics ships those fields the derivation can
 * widen without touching consumers.
 */
export function useExperimentStats(): {
  data: ExperimentStats | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { items, isLoading, isError } = useAnalyticsExperimentListAll();
  // Hide the cards entirely when the analytics list errors — without this
  // the consumer would render "0 active experiments" on a 4xx/5xx, which
  // is indistinguishable from a tenant who genuinely has none.
  const data = useMemo<ExperimentStats | undefined>(() => {
    if (isLoading || isError) return undefined;
    const activeExperiments = items.filter((i) => i.status === 'RUNNING').length;
    const needsAttention = items.filter((i) => i.needs_attention).length;
    return {
      activeExperiments,
      platformBreakdown: {},
      totalRevenueImpact: 0,
      needsAttention: { critical: 0, warning: needsAttention },
    };
  }, [items, isLoading, isError]);
  return { data, isLoading, isError };
}

/** Single experiment metadata (title, brand, segments, runningSinceDays) for the detail page header. */
export function useExperiment(id: string) {
  return useQuery({
    queryKey: queryKeys.experiments.detail(id),
    queryFn: () => fetchExperiment(id),
    // Skip the request when no id is available (e.g. an un-saved draft) so we
    // never hit `/experiments/` with an empty path segment.
    enabled: id !== '',
  });
}

/** Short enough that re-entering the page shows fresh checkpoint statuses. */
const TIMELINE_STALE_MS = 30 * 1000;

/**
 * Timeline tab checkpoint list (Day 0 → Day 28+7).
 * A 30 s `staleTime` (vs the global 5 min) means re-entering the detail page
 * refetches and surfaces the latest checkpoint statuses, while quick back-and-
 * forth navigation reuses the cache instead of re-running the analytics query
 * on every mount (it used `refetchOnMount: 'always'`).
 */
export function useExperimentTimeline(id: string) {
  return useQuery({
    queryKey: queryKeys.experiments.timeline(id),
    queryFn: ({ signal }) => fetchExperimentTimeline(id, signal),
    staleTime: TIMELINE_STALE_MS,
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/** KPI row at the top of the Details tab (days elapsed, impressions, clicks, conversions, revenue).
 *  `segment` is the encoded `?segment=` product filter (see `encodeSegmentFilter`); omitted = unscoped. */
export function useExperimentKpis(id: string, segment?: string) {
  return useQuery({
    queryKey: queryKeys.experiments.kpis(id, segment),
    queryFn: ({ signal }) => fetchExperimentKpis(id, segment, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/** Arm Comparison table — per-product averages for Control / Arm B / Arm C with lift badges and a winner column. */
export function useExperimentArmComparison(id: string, segment?: string) {
  return useQuery({
    queryKey: queryKeys.experiments.armComparison(id, segment),
    queryFn: ({ signal }) => fetchExperimentArmComparison(id, segment, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * ROAS Comparison table.
 *
 * Returns two sections (Google Ads, Google Analytics) because ROAS diverges
 * between the two attribution sources and the product team wants both shown
 * so users can reconcile the numbers rather than picking one.
 */
export function useExperimentRoasComparison(id: string, segment?: string) {
  return useQuery({
    queryKey: queryKeys.experiments.roasComparison(id, segment),
    queryFn: ({ signal }) => fetchExperimentRoasComparison(id, segment, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * Daily DID Lift — returns both the full per-metric table (`rows`) and the
 * condensed per-arm series (`trend`) in a single response, since the Trend
 * and Table views on the same card share context and are backed by the same
 * underlying daily aggregation.
 */
export function useExperimentDailyLift(id: string, segment?: string) {
  return useQuery({
    queryKey: queryKeys.experiments.dailyLift(id, segment),
    queryFn: ({ signal }) => fetchExperimentDailyLift(id, segment, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * Health → In-Stock Trends. Tracks whether Test and Control products deplete
 * at similar rates; large skew biases the DID lift results, so this card
 * flags it with a threshold KPI ("Healthy" when skew < 10%).
 */
export function useExperimentInStockTrends(id: string) {
  return useQuery({
    queryKey: queryKeys.experiments.inStockTrends(id),
    queryFn: ({ signal }) => fetchExperimentInStockTrends(id, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * Health → Category Exclusions. Surfaces categories dropped from DID analysis
 * (bad data, low volume, etc.) so users can audit which volumes were excluded
 * and trust the lift numbers.
 */
export function useExperimentCategoryExclusions(id: string) {
  return useQuery({
    queryKey: queryKeys.experiments.categoryExclusions(id),
    queryFn: ({ signal }) => fetchExperimentCategoryExclusions(id, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}

/**
 * Health → Adjusted Metrics. Re-runs the comparison using only products
 * in-stock in both arms — isolates OOS-driven noise from genuine content
 * lift, which is the number merchants actually want to act on.
 */
export function useExperimentAdjustedMetrics(id: string) {
  return useQuery({
    queryKey: queryKeys.experiments.adjustedMetrics(id),
    queryFn: ({ signal }) => fetchExperimentAdjustedMetrics(id, signal),
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}
